import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useUserId } from "@/hooks/useUserId";
import { useOnboarding } from "@/hooks/useOnboarding";
import { supabase } from "@/integrations/supabase/client";
import { Check } from "lucide-react";
import VideoTutorialHint from "@/components/onboarding/VideoTutorialHint";
import { CategoryPicker } from "@/components/categories/CategoryPicker";
import {
  parseStoredCategory,
  serializeSelection,
} from "@/constants/categoryTaxonomy";

const STEPS = [
  { id: 1, label: "Profil" },
  { id: 2, label: "Branża" },
  { id: 3, label: "Inspiracje" },
  { id: 4, label: "Konkurencja" },
  { id: 5, label: "Płatność" },
  { id: 6, label: "Start" },
];

const ONBOARDING_STORAGE_KEY = "onboarding_subcategories";

const IndustrySelection = () => {
  // Seed synchronously from localStorage so CategoryPicker mounts already at
  // the correct internal step. CategoryPicker derives its `step` state from
  // `initial` via useState's initializer — which only runs once on mount —
  // so a later async update to `initial` would NOT advance the wizard past
  // step 1 without remounting (see the `key` prop below).
  const [initialSelection, setInitialSelection] = useState<string[]>(() => {
    if (typeof window === "undefined") return [];
    const saved = window.localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (!saved) return [];
    const { subcategoryIds } = parseStoredCategory(saved);
    return subcategoryIds;
  });
  const navigate = useNavigate();
  const { toast } = useToast();
  const userId = useUserId();
  const { saveUserProfile, isLoading } = useOnboarding();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // DB fallback: if localStorage was empty (different browser, cleared
  // storage), fetch the saved category from user_profiles so a returning
  // user doesn't see a blank picker. Only fires when the synchronous seed
  // produced nothing.
  useEffect(() => {
    if (initialSelection.length > 0 || !userId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("user_profiles")
        .select("category")
        .eq("user_id", userId)
        .maybeSingle();
      if (cancelled) return;
      const { subcategoryIds } = parseStoredCategory(data?.category);
      if (subcategoryIds.length) setInitialSelection(subcategoryIds);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, initialSelection.length]);

  const handleSubmit = async (subcategoryIds: string[]) => {
    if (!userId) {
      toast({
        title: "Wymagane uwierzytelnienie",
        description: "Zaloguj się, aby kontynuować",
        variant: "destructive",
      });
      return;
    }

    if (subcategoryIds.length === 0) {
      toast({
        title: "Wybierz branżę",
        description: "Zaznacz co najmniej jedną podkategorię, aby kontynuować",
        variant: "destructive",
      });
      return;
    }

    const serialized = serializeSelection(subcategoryIds);
    localStorage.setItem(ONBOARDING_STORAGE_KEY, serialized);

    const result = await saveUserProfile(userId, {
      instagram_username: "",
      category: serialized,
    });

    if (result.success) {
      navigate("/onboarding/inspiration-sources");
    } else if (result.error?.message === "Instagram username required to create profile") {
      // Direct entry to /onboarding/industry without completing step 1 — the
      // selection is preserved in localStorage and rehydrated when the user
      // comes back here after completing step 1.
      toast({
        title: "Najpierw nazwa Instagram",
        description: "Dodaj swoją nazwę Instagram, zanim wybierzesz branżę.",
        variant: "destructive",
      });
      navigate("/onboarding/instagram");
    } else {
      toast({
        title: "Błąd zapisu",
        description: result.error?.message || "Nie udało się zapisać",
        variant: "destructive",
      });
    }
  };

  const handleCancel = () => {
    navigate("/onboarding/instagram");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FEF3EC] via-[#F9B896] to-[#F5A07A] relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-secondary/5 via-transparent to-transparent" />
      <div className="absolute top-1/4 -right-48 w-96 h-96 bg-primary/10 rounded-full blur-[120px] animate-pulse" />
      <div
        className="absolute bottom-1/4 -left-48 w-96 h-96 bg-secondary/10 rounded-full blur-[120px] animate-pulse"
        style={{ animationDelay: "1s" }}
      />

      <div className="relative z-10 max-w-2xl mx-auto px-4 py-8 md:py-16">
        {/* Outer onboarding step indicator (1-of-6) */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {STEPS.map((s, index) => (
            <div key={s.id} className="flex items-center gap-2">
              <div
                className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all
                  ${
                    s.id < 2
                      ? "bg-primary text-primary-foreground"
                      : s.id === 2
                      ? "bg-primary text-primary-foreground"
                      : "bg-white/60 text-gray-500"
                  }
                `}
              >
                {s.id < 2 ? <Check className="w-4 h-4" /> : s.id}
              </div>
              {index < STEPS.length - 1 && (
                <div
                  className={`w-8 h-0.5 ${s.id < 2 ? "bg-primary" : "bg-gray-300"}`}
                />
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-center mb-6">
          <VideoTutorialHint />
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="bg-white/85 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-5 md:p-8"
        >
          <CategoryPicker
            key={initialSelection.length === 0 ? "empty" : initialSelection.join("|")}
            initial={initialSelection}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSaving={isLoading}
            submitLabel="Kontynuuj"
            cancelLabel="Wstecz"
          />
        </motion.div>
      </div>
    </div>
  );
};

export default IndustrySelection;
